/**
 * ชนิดข้อมูลหลักของโดเมน ใช้ร่วมกันทั้ง Worker และ Web
 * ห้าม import อะไรที่ผูกกับ runtime ใด runtime หนึ่งในไฟล์นี้
 */

export const ROLES = ['field_officer', 'supervisor', 'administrator'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS_TH: Record<Role, string> = {
  field_officer: 'เจ้าหน้าที่ภาคสนาม',
  supervisor: 'หัวหน้างาน',
  administrator: 'ผู้ดูแลระบบ',
};

/** ลำดับสิทธิ์ — ตัวเลขมากกว่าคือสิทธิ์สูงกว่า ใช้เทียบแบบ >= */
export const ROLE_RANK: Record<Role, number> = {
  field_officer: 1,
  supervisor: 2,
  administrator: 3,
};

export const CAPTURE_STATUSES = ['draft', 'uploading', 'completed', 'failed', 'void'] as const;
export type CaptureStatus = (typeof CAPTURE_STATUSES)[number];

export const CAPTURE_STATUS_LABELS_TH: Record<CaptureStatus, string> = {
  draft: 'ฉบับร่าง',
  uploading: 'กำลังอัปโหลด',
  completed: 'สำเร็จ',
  failed: 'ไม่สำเร็จ',
  void: 'ยกเลิก',
};

/**
 * สถานะที่อนุญาตให้เปลี่ยนไปได้จากสถานะปัจจุบัน
 * ใช้บังคับ state machine ฝั่ง server เพื่อกัน record ค้างและกันข้อมูลไม่ตรงกัน
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<CaptureStatus, readonly CaptureStatus[]> = {
  draft: ['uploading', 'failed', 'void'],
  uploading: ['completed', 'failed', 'draft', 'void'],
  completed: ['void'],
  failed: ['uploading', 'draft', 'void'],
  void: [],
};

export const GPS_OVERRIDE_STATUSES = ['none', 'requested', 'approved', 'rejected'] as const;
export type GpsOverrideStatus = (typeof GPS_OVERRIDE_STATUSES)[number];

export const EXPORT_STATUSES = ['queued', 'running', 'ready', 'failed', 'expired'] as const;
export type ExportStatus = (typeof EXPORT_STATUSES)[number];

export interface User {
  id: string;
  externalIdentity: string | null;
  email: string;
  displayName: string;
  role: Role;
  areaCode: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** ข้อมูลผู้ใช้ที่แนบมากับ request หลังผ่าน authentication */
export interface AuthenticatedUser extends User {
  active: true;
}

export interface GpsReading {
  latitude: number;
  longitude: number;
  accuracyM: number;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  /** epoch ms จาก GeolocationPosition.timestamp */
  positionTimestamp: number;
}

export interface Capture {
  id: string;
  customerRegistrationRaw: string;
  customerRegistrationNormalized: string;
  latitude: number;
  longitude: number;
  accuracyM: number;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  positionTimestamp: string;
  capturedAt: string;
  imageObjectKey: string | null;
  imageContentType: string | null;
  imageSizeBytes: number | null;
  imageSha256: string | null;
  status: CaptureStatus;
  gpsThresholdM: number;
  gpsOverride: GpsOverrideStatus;
  gpsOverrideReason: string | null;
  gpsApprovedBy: string | null;
  gpsApprovedAt: string | null;
  createdBy: string;
  areaCode: string | null;
  idempotencyKey: string | null;
  version: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/** มุมมองที่ส่งออก API — ผ่าน field-level masking ตามบทบาทแล้ว */
export interface CaptureView extends Capture {
  createdByDisplayName?: string;
  gpsApprovedByDisplayName?: string;
  hasImage: boolean;
}

export interface AuditLogEntry {
  id: string;
  actorUserId: string | null;
  action: string;
  entityType: string;
  entityId: string;
  beforeJson: string | null;
  afterJson: string | null;
  requestId: string | null;
  ipHash: string | null;
  userAgentSummary: string | null;
  createdAt: string;
}

export interface Paginated<T> {
  items: T[];
  /** cursor แบบทึบ ส่งกลับมาที่ ?cursor= เพื่อขอหน้าถัดไป — null คือหมดแล้ว */
  nextCursor: string | null;
  /** จำนวนรวมโดยประมาณ คำนวณเฉพาะเมื่อร้องขอ เพราะ COUNT บนตารางใหญ่มีต้นทุน */
  estimatedTotal?: number;
}
